var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
/**
 * Nastavení → Sklizeň OAI (CZNA-1562) — who harvests the authority data over OAI-PMH,
 * how often, and how it goes.
 *
 * <p>From 1. 1. 2027 library systems and VIAF harvest the national authorities; NK needs
 * to see that harvesting runs and who comes for it before a library reports that nothing
 * arrived. The page only reads what `OaiHarvestController` already serves: the summary of
 * `GET /oai/harvest-statistics` and the request log of `GET /oai/harvest-log`.
 *
 * <p>Both halves cover one window, `since = now − window`: the summary is asked for
 * `since`, the log for `from = since`, so the numbers and the rows describe the same
 * requests. The instant is fixed when the window is chosen (or refreshed), not on every
 * render, so the two requests cannot drift apart. The status and client-IP filters narrow
 * the log only — the summary always counts every request of the window.
 */
import React, { useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useInfiniteQuery, useQuery } from 'react-query';
import { Accordion, AccordionSet, Button, ButtonGroup, Headline, Loading, MessageBanner, MultiColumnList, Pane, PaneMenu, Paneset, TextField, } from '@folio/stripes/components';
import { useOkapiKy } from '@folio/stripes/core';
import NavPaneLayout from '../components/NavPaneLayout';
import PaneHelpButton from '../components/PaneHelpButton';
import ScreenHelp from '../components/ScreenHelp';
import { OAI_HARVEST_HELP } from '../components/screenHelpContent';
import { fetchHarvestLog, fetchHarvestStatistics, } from '../api/oaiHarvestService';
var MSG = 'ui-czna-authorities.oaiHarvest';
var HOUR_MS = 60 * 60 * 1000;
/** The windows the owner chose (31. 10. 2026); the first is the default. */
export var HARVEST_WINDOWS = [
    { key: '24h', hours: 24, labelId: 'ui-czna-authorities.oaiHarvest.window.24h' },
    { key: '7d', hours: 7 * 24, labelId: 'ui-czna-authorities.oaiHarvest.window.7d' },
    { key: '30d', hours: 30 * 24, labelId: 'ui-czna-authorities.oaiHarvest.window.30d' },
];
/** The window's start as the ISO instant both endpoints take. */
var windowStart = function (key) {
    var hours = HARVEST_WINDOWS.find(function (w) { return w.key === key; }).hours;
    return new Date(Date.now() - hours * HOUR_MS).toISOString();
};
var STATUS_FILTERS = [
    { value: 'ALL', labelId: 'ui-czna-authorities.oaiHarvest.filter.status.ALL' },
    { value: 'ERROR', labelId: 'ui-czna-authorities.oaiHarvest.filter.status.ERROR' },
];
var STATUS_LABELS = {
    SUCCESS: 'ui-czna-authorities.oaiHarvest.status.SUCCESS',
    ERROR: 'ui-czna-authorities.oaiHarvest.status.ERROR',
};
/** The four headline numbers first, then what else the endpoint returns. */
var STAT_TILES = [
    { key: 'totalRequests', labelId: 'ui-czna-authorities.oaiHarvest.summary.totalRequests' },
    { key: 'successCount', labelId: 'ui-czna-authorities.oaiHarvest.summary.successCount', accent: '#1b7f43' },
    { key: 'errorCount', labelId: 'ui-czna-authorities.oaiHarvest.summary.errorCount', accent: '#a4123f' },
    { key: 'uniqueClientIps', labelId: 'ui-czna-authorities.oaiHarvest.summary.uniqueClientIps' },
    { key: 'totalRecordsReturned', labelId: 'ui-czna-authorities.oaiHarvest.summary.totalRecordsReturned' },
    { key: 'avgResponseTimeMs', labelId: 'ui-czna-authorities.oaiHarvest.summary.avgResponseTimeMs' },
];
var BREAKDOWNS = [
    {
        key: 'verb',
        field: 'byVerb',
        titleId: 'ui-czna-authorities.oaiHarvest.breakdown.verb.title',
        keyColumnId: 'ui-czna-authorities.oaiHarvest.breakdown.verb.column',
        emptyId: 'ui-czna-authorities.oaiHarvest.breakdown.verb.empty',
    },
    {
        key: 'errorCode',
        field: 'byErrorCode',
        titleId: 'ui-czna-authorities.oaiHarvest.breakdown.errorCode.title',
        keyColumnId: 'ui-czna-authorities.oaiHarvest.breakdown.errorCode.column',
        emptyId: 'ui-czna-authorities.oaiHarvest.breakdown.errorCode.empty',
    },
];
var LOG_COLUMNS = ['requestAt', 'clientIp', 'verb', 'params', 'recordsReturned', 'responseTimeMs', 'result'];
var LOG_COLUMN_WIDTHS = {
    requestAt: '15%',
    clientIp: '13%',
    verb: '12%',
    params: '20%',
    recordsReturned: '9%',
    responseTimeMs: '9%',
    result: '22%',
};
var tileGridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    gap: '12px',
    marginBottom: '16px',
};
/** Same tile as the corpus-quality summary strip (`Quality/SummaryStrip`). */
var tileStyle = {
    border: '1px solid #e1e7ee',
    borderRadius: '8px',
    padding: '12px 14px',
    background: '#fff',
};
var breakdownGridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
    gap: '12px',
    marginBottom: '20px',
};
var cellStyle = { padding: '2px 8px 2px 0', textAlign: 'left' };
var countCellStyle = __assign(__assign({}, cellStyle), { textAlign: 'right' });
var OaiHarvestSettings = function () {
    var _a, _b;
    var intl = useIntl();
    var ky = useOkapiKy();
    var _c = useState('24h'), windowKey = _c[0], setWindowKey = _c[1];
    var _d = useState(function () { return windowStart('24h'); }), since = _d[0], setSince = _d[1];
    var _e = useState('ALL'), statusFilter = _e[0], setStatusFilter = _e[1];
    var _f = useState(''), clientIpDraft = _f[0], setClientIpDraft = _f[1];
    var _g = useState(''), clientIp = _g[0], setClientIp = _g[1];
    var status = statusFilter === 'ERROR' ? 'ERROR' : undefined;
    var appliedClientIp = clientIp || undefined;
    var statistics = useQuery(['oai-harvest-statistics', since], function () { return fetchHarvestStatistics(ky, since); });
    var log = useInfiniteQuery(['oai-harvest-log', since, status !== null && status !== void 0 ? status : null, appliedClientIp !== null && appliedClientIp !== void 0 ? appliedClientIp : null], function (_a) {
        var pageParam = _a.pageParam;
        return fetchHarvestLog(ky, {
            from: since,
            status: status,
            clientIp: appliedClientIp, cursor: pageParam,
        });
    }, { getNextPageParam: function (lastPage) { return (lastPage.hasMore && lastPage.nextCursor ? lastPage.nextCursor : undefined); } });
    var rows = useMemo(function () { var _a, _b; return ((_b = (_a = log.data) === null || _a === void 0 ? void 0 : _a.pages) !== null && _b !== void 0 ? _b : []).flatMap(function (p) { var _a; return (_a = p.items) !== null && _a !== void 0 ? _a : []; }); }, [log.data]);
    var chooseWindow = function (key) {
        setWindowKey(key);
        setSince(windowStart(key));
    };
    var applyClientIp = function (event) {
        event === null || event === void 0 ? void 0 : event.preventDefault();
        setClientIp(clientIpDraft.trim());
    };
    var clearClientIp = function () {
        setClientIpDraft('');
        setClientIp('');
    };
    var formatInstant = function (value, withSeconds) {
        if (withSeconds === void 0) { withSeconds = false; }
        return "".concat(intl.formatDate(value, {
            day: 'numeric', month: 'numeric', year: 'numeric',
        }), " ").concat(intl.formatTime(value, __assign({ hour: '2-digit', minute: '2-digit' }, (withSeconds ? { second: '2-digit' } : {}))));
    };
    var formatMs = function (ms) { return intl.formatNumber(Math.round(ms), {
        style: 'unit', unit: 'millisecond', unitDisplay: 'short',
    }); };
    var statValue = function (stats, key) { return (key === 'avgResponseTimeMs' ? formatMs(stats.avgResponseTimeMs) : intl.formatNumber(stats[key])); };
    var renderSummary = function () {
        if (statistics.isError) {
            return (React.createElement(MessageBanner, { type: "error" },
                React.createElement(FormattedMessage, { id: "".concat(MSG, ".summary.loadError") })));
        }
        var stats = statistics.data;
        if (statistics.isLoading || !stats) {
            return React.createElement(Loading, null);
        }
        return (React.createElement(React.Fragment, null,
            React.createElement("div", { style: tileGridStyle }, STAT_TILES.map(function (tile) {
                var _a;
                return (React.createElement("div", { key: tile.key, style: tileStyle, "data-testid": "oai-harvest-stat-".concat(tile.key) },
                    React.createElement("div", { style: { fontSize: '12px', color: '#5d6b7a', marginBottom: '4px' } },
                        React.createElement(FormattedMessage, { id: tile.labelId })),
                    React.createElement("div", { style: { fontSize: '22px', fontWeight: 700, color: (_a = tile.accent) !== null && _a !== void 0 ? _a : '#1f2d3a' } }, statValue(stats, tile.key))));
            })),
            React.createElement("div", { style: breakdownGridStyle }, BREAKDOWNS.map(function (breakdown) {
                var _a;
                var entries = Object.entries((_a = stats[breakdown.field]) !== null && _a !== void 0 ? _a : {})
                    .sort(function (_a, _b) {
                    var a = _a[0], x = _a[1];
                    var b = _b[0], y = _b[1];
                    return y - x || a.localeCompare(b);
                });
                return (React.createElement("section", { key: breakdown.key, style: tileStyle, "data-testid": "oai-harvest-breakdown-".concat(breakdown.key) },
                    React.createElement(Headline, { size: "small", margin: "xx-small", tag: "h4" },
                        React.createElement(FormattedMessage, { id: breakdown.titleId })),
                    entries.length === 0 ? (React.createElement("span", { style: { color: '#5d6b7a' } },
                        React.createElement(FormattedMessage, { id: breakdown.emptyId }))) : (React.createElement("table", { style: { borderCollapse: 'collapse', width: '100%' } },
                        React.createElement("thead", null,
                            React.createElement("tr", null,
                                React.createElement("th", { style: cellStyle }, intl.formatMessage({ id: breakdown.keyColumnId })),
                                React.createElement("th", { style: countCellStyle }, intl.formatMessage({ id: "".concat(MSG, ".breakdown.count") })))),
                        React.createElement("tbody", null, entries.map(function (_a) {
                            var name = _a[0], count = _a[1];
                            return (React.createElement("tr", { key: name },
                                React.createElement("td", { style: cellStyle },
                                    React.createElement("code", null, name)),
                                React.createElement("td", { style: countCellStyle }, intl.formatNumber(count))));
                        }))))));
            }))));
    };
    /** The OAI arguments of the request, as the harvester sent them. */
    var formatParams = function (e) {
        var parts = [];
        if (e.metadataPrefix)
            parts.push(e.metadataPrefix);
        if (e.setSpec)
            parts.push("set=".concat(e.setSpec));
        if (e.fromParam)
            parts.push("from=".concat(intl.formatDate(e.fromParam)));
        if (e.untilParam)
            parts.push("until=".concat(intl.formatDate(e.untilParam)));
        if (e.resumptionTokenUsed)
            parts.push(intl.formatMessage({ id: "".concat(MSG, ".log.resumptionToken") }));
        return parts.length === 0 ? '—' : parts.join(' · ');
    };
    var columnMapping = {
        requestAt: React.createElement(FormattedMessage, { id: "".concat(MSG, ".log.column.requestAt") }),
        clientIp: React.createElement(FormattedMessage, { id: "".concat(MSG, ".log.column.clientIp") }),
        verb: React.createElement(FormattedMessage, { id: "".concat(MSG, ".log.column.verb") }),
        params: React.createElement(FormattedMessage, { id: "".concat(MSG, ".log.column.params") }),
        recordsReturned: React.createElement(FormattedMessage, { id: "".concat(MSG, ".log.column.recordsReturned") }),
        responseTimeMs: React.createElement(FormattedMessage, { id: "".concat(MSG, ".log.column.responseTimeMs") }),
        result: React.createElement(FormattedMessage, { id: "".concat(MSG, ".log.column.result") }),
    };
    var formatter = {
        requestAt: function (e) { return formatInstant(e.requestAt, true); },
        clientIp: function (e) { var _a; return (_a = e.clientIp) !== null && _a !== void 0 ? _a : '—'; },
        verb: function (e) { var _a; return (_a = e.verb) !== null && _a !== void 0 ? _a : '—'; },
        params: formatParams,
        recordsReturned: function (e) { return intl.formatNumber(e.recordsReturned); },
        responseTimeMs: function (e) { return formatMs(e.responseTimeMs); },
        result: function (e) {
            var _a, _b, _c;
            return (e.status === 'ERROR' ? (React.createElement("span", { title: (_a = e.errorMessage) !== null && _a !== void 0 ? _a : undefined, style: { color: '#a4123f' } },
                React.createElement("strong", null, (_b = e.errorCode) !== null && _b !== void 0 ? _b : intl.formatMessage({ id: STATUS_LABELS.ERROR })),
                e.errorMessage ? React.createElement("span", { style: { display: 'block', fontSize: '12px' } }, e.errorMessage) : null)) : intl.formatMessage({ id: (_c = STATUS_LABELS[e.status]) !== null && _c !== void 0 ? _c : STATUS_LABELS.SUCCESS }));
        },
    };
    var filtered = status !== undefined || appliedClientIp !== undefined;
    var loadedPages = (_b = (_a = log.data) === null || _a === void 0 ? void 0 : _a.pages.length) !== null && _b !== void 0 ? _b : 0;
    // react-query 3 keeps the loaded pages when a next-page fetch fails and has no separate
    // flag for it: an error with pages in hand is "Načíst další" failing, not the log.
    var nextPageFailed = log.isError && loadedPages > 0;
    var renderLog = function () {
        if (log.isError && loadedPages === 0) {
            return (React.createElement(MessageBanner, { type: "error" },
                React.createElement(FormattedMessage, { id: "".concat(MSG, ".log.loadError") })));
        }
        return (React.createElement(React.Fragment, null,
            React.createElement(MultiColumnList, { id: "oai-harvest-log", contentData: rows, columnMapping: columnMapping, columnWidths: LOG_COLUMN_WIDTHS, visibleColumns: LOG_COLUMNS, formatter: formatter, nonInteractiveHeaders: LOG_COLUMNS, loading: log.isLoading, isEmptyMessage: React.createElement(FormattedMessage, { id: filtered ? "".concat(MSG, ".log.emptyFiltered") : "".concat(MSG, ".log.empty") }) }),
            log.hasNextPage && (React.createElement("div", { style: { marginTop: '12px', textAlign: 'center' } },
                nextPageFailed && !log.isFetchingNextPage && (React.createElement("div", { style: { marginBottom: '8px' }, "data-testid": "oai-harvest-log-load-more-error" },
                    React.createElement(MessageBanner, { type: "error" },
                        React.createElement(FormattedMessage, { id: "".concat(MSG, ".log.loadMoreError") })))),
                React.createElement(Button, { onClick: function () { return log.fetchNextPage(); }, disabled: log.isFetchingNextPage, "data-testid": "oai-harvest-log-load-more" },
                    React.createElement(FormattedMessage, { id: "".concat(MSG, ".log.loadMore") }))))));
    };
    var lastMenu = (React.createElement(PaneMenu, null,
        React.createElement(PaneHelpButton, null,
            React.createElement(ScreenHelp, { spec: OAI_HARVEST_HELP })),
        React.createElement(Button, { marginBottom0: true, onClick: function () { return setSince(windowStart(windowKey)); }, "data-testid": "oai-harvest-refresh" },
            React.createElement(FormattedMessage, { id: "".concat(MSG, ".refresh") }))));
    return (React.createElement(Paneset, null,
        React.createElement(Pane, { id: "oai-harvest-filter-pane", defaultWidth: "22%", paneTitle: React.createElement(FormattedMessage, { id: "ui-czna-authorities.pane.navigation" }) },
            React.createElement(NavPaneLayout, null,
                React.createElement("hr", { style: { border: 'none', borderTop: '1px solid #d9d9d9', margin: '12px 0' } }),
                React.createElement(AccordionSet, null,
                    React.createElement(Accordion, { label: intl.formatMessage({ id: "".concat(MSG, ".filter.window") }), separator: false },
                        React.createElement(ButtonGroup, { fullWidth: true }, HARVEST_WINDOWS.map(function (w) { return (React.createElement(Button, { key: w.key, buttonStyle: windowKey === w.key ? 'primary' : 'default', "aria-pressed": windowKey === w.key, onClick: function () { return chooseWindow(w.key); }, "data-testid": "oai-harvest-window-".concat(w.key) },
                            React.createElement(FormattedMessage, { id: w.labelId }))); }))),
                    React.createElement(Accordion, { label: intl.formatMessage({ id: "".concat(MSG, ".filter.status") }) },
                        React.createElement(ButtonGroup, { fullWidth: true }, STATUS_FILTERS.map(function (f) { return (React.createElement(Button, { key: f.value, buttonStyle: statusFilter === f.value ? 'primary' : 'default', "aria-pressed": statusFilter === f.value, onClick: function () { return setStatusFilter(f.value); }, "data-testid": "oai-harvest-status-".concat(f.value) },
                            React.createElement(FormattedMessage, { id: f.labelId }))); }))),
                    React.createElement(Accordion, { label: intl.formatMessage({ id: "".concat(MSG, ".filter.clientIp") }) },
                        React.createElement("form", { onSubmit: applyClientIp, "data-testid": "oai-harvest-client-ip" },
                            React.createElement(TextField, { name: "clientIp", value: clientIpDraft, onChange: function (e) { return setClientIpDraft(e.target.value); }, placeholder: intl.formatMessage({ id: "".concat(MSG, ".filter.clientIp.placeholder") }), "aria-label": intl.formatMessage({ id: "".concat(MSG, ".filter.clientIp") }) }),
                            React.createElement("div", { style: { display: 'flex', gap: '8px' } },
                                React.createElement(Button, { type: "submit", buttonStyle: "primary", marginBottom0: true, "data-testid": "oai-harvest-client-ip-apply" },
                                    React.createElement(FormattedMessage, { id: "".concat(MSG, ".filter.clientIp.apply") })),
                                React.createElement(Button, { marginBottom0: true, onClick: clearClientIp, disabled: !clientIpDraft && !clientIp, "data-testid": "oai-harvest-client-ip-clear" },
                                    React.createElement(FormattedMessage, { id: "".concat(MSG, ".filter.clientIp.clear") })))))))),
        React.createElement(Pane, { id: "oai-harvest-content-pane", defaultWidth: "fill", paneTitle: React.createElement(FormattedMessage, { id: "".concat(MSG, ".title") }), paneSub: intl.formatMessage({ id: "".concat(MSG, ".since") }, { since: formatInstant(since) }), lastMenu: lastMenu },
            React.createElement(Headline, { size: "medium", margin: "small", tag: "h3" },
                React.createElement(FormattedMessage, { id: "".concat(MSG, ".summary.title") })),
            renderSummary(),
            React.createElement(Headline, { size: "medium", margin: "small", tag: "h3" },
                React.createElement(FormattedMessage, { id: "".concat(MSG, ".log.title") })),
            renderLog())));
};
export default OaiHarvestSettings;
